// Shared pieces of the Options Analysis vertical (read-only).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CircleDot, Pause, Play, Power, ShieldCheck } from "lucide-react";
import optionsApi, { optionsError } from "@/lib/optionsApi";
import { num } from "@/lib/format";
import { toast } from "sonner";

/** Poll `path` every `ms`, pausing while the tab is hidden or `paused` is true. */
export function usePoll(path, { ms = 2000, paused = false, params } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const key = JSON.stringify(params || {});
  const alive = useRef(true);            // false once the component unmounted or the query changed
  const load = useCallback(() => {
    if (!path) return Promise.resolve();
    return optionsApi.get(path, { params })
      .then((r) => { if (alive.current) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive.current) setError(optionsError(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key]);
  useEffect(() => {
    const flag = alive;
    flag.current = true;
    load();
    if (paused) return () => { flag.current = false; };
    const t = setInterval(() => { if (!document.hidden) load(); }, ms);
    return () => { flag.current = false; clearInterval(t); };
  }, [load, ms, paused]);
  return { data, error, reload: load };
}

export const px = (v) => (v === null || v === undefined ? "—" : num(v, 2));
export const qty = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN"));
export const age = (s) => {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${s.toFixed(1)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.round((s % 86400) / 3600)}h`;
};
export const istTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", {
  timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
}) + " IST" : "—");

// LTP-mode data warnings of one pair (codes like "CE_LTP_OLD_TRADE") as readable text, with the
// actual last-trade time / age of the leg — a raw LTP opportunity is shown, never passed off as fresh.
export function ltpWarningText(warnings, r) {
  return (warnings || []).map((w) => {
    const side = w.slice(0, 2), code = w.slice(3), leg = (side === "CE" ? r?.ce : r?.pe) || {};
    if (code === "LTP_OLD_TRADE") return `${side} LTP stale (last trade ${istTime(leg.last_trade_time)}, ${age(leg.trade_age_s)} ago)`;
    if (code === "TICK_STALE") return `${side} no update for ${age(leg.tick_age_s)}`;
    if (code === "NO_VOLUME_TODAY") return `${side} no volume today`;
    return `${side} ${code}`;
  }).join(", ");
}

export function StatusPill({ status, label }) {
  const good = status === "OK" || status === "QUALIFIED" || status === "RUNNING" || status === "LIVE";
  const warn = ["WAITING_FOR_TICKS", "STARTING", "MARKET_CLOSED", "SPOT_STALE", "REPLAY", "CONNECTING"].includes(status);
  const cls = good ? "bg-[#00C805]/15 text-[#00C805]" : warn ? "bg-[#FFCC00]/15 text-[#b38f00]" : "bg-[#FF3B30]/15 text-[#FF3B30]";
  return <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-bold mono ${cls}`}>{label || status || "—"}</span>;
}

/** A leg's validity in one mode: OK, or the reason code the scanner used to reject it. */
export function LegStatus({ value }) {
  if (!value || value === "OK") return <span className="text-[#00C805] text-[10px] font-bold">OK</span>;
  return <span className="text-[#FF3B30] text-[10px] font-bold mono">{value}</span>;
}

/** Neutral market context line: which market, where the data comes from, read-only. */
export function MarketContext({ market, universeLabel, feedState }) {
  if (!market) return null;
  const source = feedState === "REPLAY" ? "Replayed broker data (test harness)" : "Live broker data";
  return (
    <div className="flex items-center gap-2 text-xs" data-testid="options-market-context">
      <span className="px-2 py-0.5 rounded-sm font-bold bg-[#2563EB] text-white" data-testid="options-market-badge">{market}</span>
      <span className="text-[var(--term-secondary)]">{universeLabel ? `${universeLabel} · ` : ""}{source} · Read-only analysis</span>
    </div>
  );
}

/** One lot's economics: broker quantity × price units = cash per 1.00 of price (contracts.py). */
export function EconomicsNote({ e }) {
  if (!e) return null;
  const pending = !e.economic_multiplier;
  return (
    <span className="mono text-[11px]" data-testid="options-economics"
      title={`source ${e.source}${e.reference_contract_size != null ? ` · Data_Misc ${e.reference_contract_size} (${e.reference_agrees ? "agrees" : "DIFFERS"})` : ""}`}>
      1 lot = broker qty {e.broker_order_quantity ?? "—"} × {e.price_units_per_quantity ?? "?"} price units
      = <b>{pending ? "multiplier pending" : `${e.economic_multiplier} per 1.00`}</b>
      {pending && " (per-lot values shown once the broker calculator has measured it)"}
    </span>
  );
}

/** Scanner status + start/stop. Start/stop only open/close the analysis feed — never an order. */
export function ScannerStatusBar({ status, onChanged }) {
  const rt = status?.runtime || {};
  const running = rt.status === "RUNNING";
  const feed = rt.feed || {};
  const [busy, setBusy] = useState(false);
  const act = (what) => {
    setBusy(true);
    optionsApi.post(`/scanner/${what}`)
      .then(() => { toast.success(what === "start" ? "Options scanner started" : "Options scanner stopped"); onChanged && onChanged(); })
      .catch((e) => toast.error(optionsError(e)))
      .finally(() => setBusy(false));
  };
  const failed = (rt.gate?.checks || []).filter((c) => !c.ok);
  return (
    <div className="term-panel rounded-sm px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="options-status-bar">
      <span className="flex items-center gap-1.5 font-bold"><ShieldCheck className="h-3.5 w-3.5 text-[#00C805]" /> READ-ONLY · no order placement</span>
      <span className="flex items-center gap-1">Scanner <StatusPill status={rt.status || "STOPPED"} /></span>
      <span className="flex items-center gap-1">Feed <StatusPill status={rt.feed_state === "LIVE" ? (feed.connected ? "LIVE" : "CONNECTING") : (rt.feed_state || "NONE")} /></span>
      {feed.kind && <span className="text-[var(--term-muted)] mono">
        subscribed {feed.subscribed}/{feed.cap} · data {feed.tokens_with_data} · fresh≤10s {feed.fresh_10s} · last packet {age(feed.last_packet_age_s)}
        {feed.truncated ? ` · CAP TRUNCATED ${feed.truncated}` : ""}
      </span>}
      {rt.scanner && <span className="text-[var(--term-muted)] mono">cycle {rt.scanner.cycle_ms_last}ms (max {rt.scanner.cycle_ms_max}) · v{rt.scanner.version}</span>}
      <span className="flex-1" />
      {running
        ? <button disabled={busy} onClick={() => act("stop")} data-testid="options-stop"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-bold bg-[var(--term-s3)] border border-[var(--term-border-strong)]"><Power className="h-3 w-3" /> Stop scanner</button>
        : <button disabled={busy} onClick={() => act("start")} data-testid="options-start"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-bold bg-[#2563EB] text-white"><Play className="h-3 w-3" /> Start scanner</button>}
      {(rt.message || failed.length > 0) && (
        <div className="w-full flex items-start gap-1.5 text-[11px] text-[#b38f00]">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{rt.message}{failed.length > 0 && <> — failed checks: {failed.map((c) => `${c.check} (${c.detail})`).join("; ")}</>}</span>
        </div>
      )}
    </div>
  );
}

export function PauseButton({ paused, setPaused }) {
  return (
    <button onClick={() => setPaused(!paused)} data-testid="options-pause"
      className="flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-bold bg-[var(--term-s3)] border border-[var(--term-border-strong)]">
      {paused ? <><Play className="h-3 w-3" /> Resume view</> : <><Pause className="h-3 w-3" /> Pause view</>}
    </button>
  );
}

export function UnderlyingPicker({ underlyings, value, onChange, testId }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-1 text-xs">
      {(underlyings || []).map((u) => (
        <option key={u.underlying} value={u.underlying}>{u.underlying}{u.expiry ? ` · ${u.expiry}` : ""} · {u.status}</option>
      ))}
    </select>
  );
}

export function Dot({ ok }) {
  return <CircleDot className={`h-3 w-3 ${ok ? "text-[#00C805]" : "text-slate-400"}`} />;
}

const inr = (v) => (v === null || v === undefined ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const basisLabel = (c) => `${c?.product || ""} · ${c?.price_basis === "MARKET" ? "MARKET" : "LIMIT at offer"}`;

// Broker capital (O5): Zerodha's margin CALCULATOR figure for BUY CE + BUY PE at one broker lot.
// Opportunity % on Capital is an analysis metric, never realised P&L.
export function CapitalCell({ c, mode }) {
  if (!c || c.status === "DISABLED") return <span className="text-[var(--term-muted)]">off</span>;
  if (c.status === "OK" || c.status === "STALE") {
    const pct = c.pct_on_capital?.[mode];
    return (
      <span title={`${basisLabel(c)} · broker figure ${age(c.age_s)} old · ${c.label}`} className={c.status === "STALE" ? "opacity-60" : ""}>
        {inr(c.capital_required)} · <b className={pct > 0 ? "text-[#00C805]" : pct < 0 ? "text-[#FF3B30]" : ""}>{pct === null || pct === undefined ? "—" : `${pct.toFixed(2)}%`}</b>
        {c.status === "STALE" && " (stale)"}
        {c.priced_below_limit && <span className="ml-1 text-[9px] font-bold px-1 rounded-sm bg-[#F59E0B]/20 text-[#B45309]"
          title={`Broker priced a leg at its LTP, below the LIMIT sent: premium at the limit prices would be ${inr(c.limit_premium)}`}>BELOW LIMIT</span>}
      </span>
    );
  }
  return <span className="text-[var(--term-muted)]" title={c.error || ""}>{c.status === "PENDING" ? "pending" : c.status}</span>;
}

export function CapitalPanel({ c, onRefresh, busy }) {
  return (
    <div className="term-panel rounded-sm p-3 text-xs mono space-y-1" data-testid="options-capital-panel">
      <div className="flex items-center gap-2">
        <b>Broker capital</b><span className="text-[var(--term-muted)]">{c ? basisLabel(c) : ""} · {c?.status}{c?.age_s !== undefined ? ` · ${age(c.age_s)} old` : ""}</span>
        {onRefresh && <button onClick={onRefresh} disabled={busy} className="ml-auto px-2 py-0.5 rounded-sm border border-[var(--term-border-strong)]" data-testid="options-capital-refresh">{busy ? "…" : "Get from broker"}</button>}
      </div>
      {(c?.status === "OK" || c?.status === "STALE") ? (
        <>
          <div>Capital required <b>{inr(c.capital_required)}</b> (premium {inr(c.option_premium)} · SPAN {inr(c.span)} · exposure {inr(c.exposure)}) for {c.quantity} qty per leg</div>
          {c.priced_below_limit && <div className="text-[#B45309]">WARNING: the broker calculator priced a leg at its LTP, below the LIMIT sent. Premium at the limit prices would be {inr(c.limit_premium)} — the capital and % on capital shown understate it.</div>}
          <div>Priced at CE {px(c.prices?.ce)} / PE {px(c.prices?.pe)} · % on capital LTP <b>{c.pct_on_capital?.LTP?.toFixed(2) ?? "—"}%</b> · OFFER <b>{c.pct_on_capital?.OFFER?.toFixed(2) ?? "—"}%</b></div>
          <div>Charges returned by broker (entry, display only) {inr(c.charges_total)}{(c.charges || []).map((x) => ` · ${x.tradingsymbol} ${inr(x.total)}`).join("")}</div>
          <div className="text-[10px] text-[var(--term-muted)]">% on capital = Opportunity per lot ÷ broker capital × 100 — {c.label}.</div>
        </>
      ) : <div className="text-[var(--term-muted)]">{c?.error || "Not fetched yet — the top pairs refresh automatically; use the button for this pair."}</div>}
    </div>
  );
}
